import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";

import arrowIcon from "../../../assets/previous_icon.png";
import editIcon from "../../../assets/edit_icon.png";
import closeIcon from "../../../assets/close_icon.png";
import saveIcon from "../../../assets/save_icon.png";
import checkIcon from "../../../assets/check_icon.png";
import printIcon from "../../../assets/printer_icon.png";

import CustomButton from "../../../components/CustomButton";
import AreYouSureModal from "../../../components/AreYouSureModal";
import { useNavigate } from "react-router-dom";
import CustomInputWithUnit from "../../../components/CustomInputWithUnit";
import { useReactToPrint } from "react-to-print";
import PrintablePaperOrder from ".././PrintablePaperOrder";
import useLanguage from "../../../utils/useLanguage";
import InternalPrintableOrderView from "./InternalPrintableOrderView";
import { create } from "@mui/material/styles/createTransitions";
import { formatDate } from "../../../utils/toDate";
import CustomInput from "../../../components/CustomInput";
import CustomCheckbox from "../../../components/CustomCheckbox";

const UnlinkedPaperOrderList = ({
  data,
  setData,
  orderStatus = "UNCOMPLETED",
}) => {
  // HOOKS
  const { t } = useTranslation("general");
  const navigate = useNavigate();
  const { currentDirection } = useLanguage();

  // STATES
  const [selectedItems, setSelectedItems] = useState({});
  const [collapsed, setCollapsed] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [editableData, setEditableData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [areYouSureModal, setAreYouSureModal] = useState(false);
  const printRef = useRef();

  // Transform backend response - group by paperCode
  const transformedData = data?.paperOrderItems?.reduce((acc, item) => {
    const rollCode = item.rollCode;
    if (!rollCode) return acc;

    if (!acc[rollCode]) {
      acc[rollCode] = {
        code: rollCode,
        items: [],
      };
    }

    acc[rollCode].items.push({
      id: item.id,
      paperName: item.paperCode,
      dimension: item.dimension,
      amount: item.amount,
      // amountOfRolls: item.amountOfRolls,
      paperThickness: item.paperThickness,
      measuringUnit: item.measuringUnit,
      paperType: item.paperType,
    });

    return acc;
  }, {});

  const paperList = Object.values(
    isEditMode && editableData ? editableData : transformedData || {}
  );

  const handleCheckboxChange = (paperCode, itemId) => {
    setSelectedItems((prev) => ({
      ...prev,
      [paperCode]: {
        ...prev[paperCode],
        [itemId]: !prev[paperCode]?.[itemId],
      },
    }));
  };

  const toggleCollapse = (paperCode) => {
    setCollapsed((prev) => ({
      ...prev,
      [paperCode]: !prev[paperCode],
    }));
  };

  const handleEdit = () => {
    const prechecked = {};
    const deepCopy = {};

    Object.entries(transformedData).forEach(([paperCode, paper]) => {
      prechecked[paperCode] = {};
      deepCopy[paperCode] = {
        code: paper.code,
        items: paper.items.map((item) => {
          prechecked[paperCode][item.id] = true;
          return { ...item };
        }),
      };
    });

    setSelectedItems(prechecked);
    setEditableData(deepCopy);
    setIsEditMode(true);
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setEditableData(null);
    setSelectedItems({});
  };

  const handleSave = async () => {
    if (!editableData) return;

    setLoading(true);

    try {
      // Prepare the paperOrderItems array with only the items that are checked
      const paperOrderItems = [];

      Object.entries(editableData).forEach(([paperCode, paper]) => {
        paper.items.forEach((item) => {
          const isChecked = selectedItems?.[paperCode]?.[item.id];
          if (isChecked) {
            paperOrderItems.push({
              id: item.id,
              paperCode: paperCode,
              paperName: item.paperName,
              measuringUnit: item.measuringUnit,
              dimension: parseFloat(item.dimension),
              amount: parseFloat(item.amount),
              // amountOfRolls: parseFloat(item.amountOfRolls),
              paperThickness: parseFloat(item.paperThickness),
              paperType: item.paperType,
            });
          }
        });
      });

      const response = await axios.put(
        `${process.env.REACT_APP_URL}/api/paper/${data?.id}`,
        {
          paperOrderItems,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.status === 200) {
        setData(response.data.data);
        setIsEditMode(false);
        setEditableData(null);
        setSelectedItems({});
      }
    } catch (error) {
      console.error("Save failed:", error);
      alert(t("failedChanges"));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (paperCode, itemId, field, value) => {
    setEditableData((prev) => {
      const updated = { ...prev };
      updated[paperCode].items = updated[paperCode].items.map((item) =>
        item.id === itemId ? { ...item, [field]: value } : item
      );
      return updated;
    });
  };

  const handleConfirmOrder = async () => {
    setLoading(true);
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_URL}/api/paper/${data?.id}`,
        {
          organizationId: data?.organizationId,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          params: {
            id: data?.id,
          },
        }
      );

      if (response.status === 200) {
        setAreYouSureModal(false);
        navigate(-1);
      }
    } catch (error) {
      console.error("Error confirming order:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAttemptToConfirmReceived = () => setAreYouSureModal(true);

  const preparePrintData = () => {
    return {
      orderItems: paperList.flatMap((paper) =>
        paper.items.map((item) => ({
          paperCode: paper.code,
          paperName: item.paperName,
          dimension: item.dimension,
          amount: item.amount,
          // amountOfRolls: item.amountOfRolls,
          paperThickness: item.paperThickness,
          paperType: item.paperType,
        }))
      ),
    };
  };

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    pageStyle: `
      @page { 
          size: A4; 
          margin: 5mm;
          width: 21cm;
          height: 29.7cm;
          direction: ${currentDirection}
      }
      @media print {
        body { 
          -webkit-print-color-adjust: exact; 
          padding: 10mm;
        }
        .printable-order-view { 
          display: flex !important;
          direction: ${currentDirection}
        }
      }
    `,
  });

  return (
    <div className="c-fabric-table">
      <div className="o-row o-row--horizontal-align_space-between">
        <div className="u-margin--bottom_500">
          <h1 className="t-headline-1">
            {t("orderNumber")}: {data?.orderNumber}
          </h1>
          <div className="o-column o-column--gap-size_400">
            <span>
              {t("orderDate")}: {formatDate(data.orderDate)}
            </span>
            <span>
              {t("notes")}: {data?.notes || t("noNotes")}
            </span>
          </div>
        </div>
        {orderStatus === "UNCOMPLETED" && (
          <>
            {!isEditMode ? (
              <CustomButton
                variant="blank"
                title={t("edit")}
                onClick={handleEdit}
                icon={editIcon}
                iconSize={18}
              />
            ) : (
              <div className="o-row o-row--gap-size_300">
                <CustomButton
                  title={t("cancel")}
                  onClick={handleCancel}
                  variant="blank"
                  icon={closeIcon}
                  iconSize={14}
                />
                <CustomButton
                  title={loading ? t("saving") : t("save")}
                  onClick={handleSave}
                  icon={saveIcon}
                  iconSize={16}
                  disabled={loading}
                />
              </div>
            )}
          </>
        )}
      </div>

      <div className="o-column o-column--gap-size_300">
        {paperList.map((paper) => (
          <div key={paper.code} className="c-fabric-block">
            <div
              className="c-fabric-header"
              onClick={() => toggleCollapse(paper.code)}
            >
              <span
                className="c-collapse-icon"
                style={{
                  cursor: "pointer",
                  transform: collapsed[paper.code]
                    ? "rotate(0deg)"
                    : "rotate(-90deg)",
                  transition: "transform 0.3s ease",
                }}
              >
                <img src={arrowIcon} alt="Collapse" style={{ width: 16 }} />
              </span>
              <span className="c-fabric-code">
                {t("categoryCode")}: {paper.code}
              </span>
            </div>

            {!collapsed[paper.code] && (
              <table className="c-fabric-inner-table">
                <thead>
                  <tr>
                    {isEditMode && <th></th>}
                    <th>{t("categoryName")}</th>
                    <th>{t("paperType")}</th>
                    <th>{t("dimension")}</th>
                    <th>{t("paperThickness")}</th>
                    <th>{t("quantity")}</th>
                    {/* <th>{t("amountOfRolls")}</th> */}
                  </tr>
                </thead>
                <tbody>
                  {paper?.items?.map((item) => (
                    <tr key={item.id}>
                      {isEditMode && (
                        <td>
                          <CustomCheckbox
                            checked={!!selectedItems[paper.code]?.[item.id]}
                            onChange={() =>
                              handleCheckboxChange(paper.code, item.id)
                            }
                          />
                        </td>
                      )}
                      <td>{item.paperName}</td>
                      <td>
                        {isEditMode ? (
                          <CustomInput
                            type="text"
                            value={item.paperType}
                            onChange={(e) =>
                              handleInputChange(
                                paper.code,
                                item.id,
                                "paperType",
                                e.target.value
                              )
                            }
                            flex
                          />
                        ) : (
                          item.paperType || "---"
                        )}
                      </td>
                      <td>
                        {isEditMode ? (
                          <CustomInputWithUnit
                            type="moneyV2"
                            value={item.dimension}
                            onChange={(e) =>
                              handleInputChange(
                                paper.code,
                                item.id,
                                "dimension",
                                e.target.value
                              )
                            }
                            flex
                            unit={t("cm")}
                          />
                        ) : item.dimension ? (
                          item.dimension + ` ${t("cm")}`
                        ) : (
                          "---"
                        )}
                      </td>

                      <td>
                        {isEditMode ? (
                          <CustomInputWithUnit
                            type="moneyV2"
                            value={item.paperThickness}
                            onChange={(e) =>
                              handleInputChange(
                                paper.code,
                                item.id,
                                "paperThickness",
                                e.target.value
                              )
                            }
                            unit={t("gram")}
                            flex
                          />
                        ) : item.paperThickness ? (
                          `${item.paperThickness} ${t("gram")}`
                        ) : (
                          "---"
                        )}
                      </td>

                      <td>
                        {isEditMode ? (
                          <CustomInputWithUnit
                            type="moneyV2"
                            value={item.amount}
                            onChange={(e) =>
                              handleInputChange(
                                paper.code,
                                item.id,
                                "amount",
                                e.target.value
                              )
                            }
                            flex
                            unit={t("ton")}
                          />
                        ) : (
                          item.amount + ` ${t("ton")}`
                        )}
                      </td>
                      {/* <td>
                        {isEditMode ? (
                          <CustomInputWithUnit
                            type="moneyV2"
                            value={item.amountOfRolls}
                            onChange={(e) =>
                              handleInputChange(
                                paper.code,
                                item.id,
                                "amountOfRolls",
                                e.target.value
                              )
                            }
                            flex
                            unit={t("rollUnit")}
                          />
                        ) : (
                          item.amountOfRolls + ` ${t("rollUnit")}`
                        )}
                      </td> */}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))}
      </div>

      {orderStatus == "UNCOMPLETED" && (
        <div className="u-margin--top_500 u-margin--bottom_500 o-row o-row--horizontal-align_end o-row--gap-size_400">
          {!isEditMode && (
            <>
              <CustomButton
                variant="gray"
                title={t("print")}
                onClick={handlePrint}
                icon={printIcon}
                iconSize={18}
              />

              <CustomButton
                title={t("confirmOrder")}
                variant="green"
                onClick={handleAttemptToConfirmReceived}
                icon={checkIcon}
                iconSize={18}
              />
            </>
          )}

          {areYouSureModal ? (
            <AreYouSureModal
              onClose={() => setAreYouSureModal(false)}
              onConfirm={handleConfirmOrder}
              customMessage="areYouSureTheOrderIsCompleted"
              closeTitle="cancel"
              confirmTitle="confirm"
              loading={loading}
              confirm
              confirmIconType="whiteCheck"

            />
          ) : null}
        </div>
      )}

      <InternalPrintableOrderView
        ref={printRef}
        data={preparePrintData()}
        measuringUnit={{
          title: data?.paperOrderItems?.[0]?.measuringUnit || "kg",
        }}
        orderDetails={{
          orderNumber: data?.orderNumber,
          date: new Date(data?.orderDate).toLocaleDateString(),
          companyName: localStorage.getItem("organizationName"),
          notes: data?.notes,
          createdBy: data?.createdBy || "",
        }}
      />
    </div>
  );
};

export default UnlinkedPaperOrderList;
